import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n";
import LogoBrand from "../components/LogoBrand";
import LanguageSwitch from "../components/LanguageSwitch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(res.data?.message || t("auth.reset_link_sent"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("auth.something_went_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative" style={{
        backgroundImage: "url(https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBjbGVhbnxlbnwwfHx8fDE3ODU5MTcwMDd8MA&ixlib=rb-4.1.0&q=85)",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-slate-900/40" />
      </div>
      <div className="flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <div className="[&_button]:!bg-white [&_button]:!text-slate-900 [&_button]:!border-slate-300">
              <LanguageSwitch variant="light" />
            </div>
          </div>
          <div className="flex flex-col items-center mb-8"><LogoBrand variant="login" /></div>
          <Card className="p-6 md:p-8 border-slate-200 shadow-sm" data-testid="forgot-password-card">
            <h1 className="font-serif text-2xl text-slate-900 mb-1">{t("auth.forgot_title")}</h1>
            <p className="text-sm text-slate-500 mb-6">
              {sent ? t("auth.reset_check_email") : t("auth.forgot_help")}
            </p>
            {!sent ? (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">{t("auth.email")}</Label>
                  <Input id="forgot-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 mt-1" data-testid="forgot-email"
                    placeholder="name@varunainn.com" />
                </div>
                <Button type="submit" disabled={busy}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="forgot-submit">
                  {busy ? t("auth.sending") : t("auth.send_reset_link")}
                </Button>
              </form>
            ) : (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900" data-testid="forgot-sent-msg">
                {t("auth.reset_sent_body")}
              </div>
            )}
            <div className="mt-6 flex items-center justify-between text-sm">
              <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="back-to-login">
                ← {t("auth.back_to_signin")}
              </Link>
              {sent && (
                <button type="button" onClick={() => { setSent(false); setEmail(""); }}
                  className="text-slate-600 hover:text-slate-900" data-testid="forgot-try-another">
                  {t("auth.try_another_email")}
                </button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
